import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Modal, Form, Input, Select, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { getAccountList, saveAccount, getRoleList } from '../../api/system';
import { hasPerm } from '../../hooks/usePermission';

const AccountManage = () => {
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [roleOptions, setRoleOptions] = useState([]);
    const [form] = Form.useForm();

    const fetchList = async () => {
        setLoading(true);
        try {
            const res = await getAccountList();
            if (res.code === 0) {
                setDataSource(res.data.list);
            } else {
                message.error(res.message || '获取列表失败');
            }
        } catch (error) {
            console.error('Fetch account list failed:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
        const fetchRoles = async () => {
            const res = await getRoleList();
            if (res.code === 0) {
                setRoleOptions(res.data.list.map((r) => ({ value: r.name, label: r.name })));
            }
        };
        fetchRoles();
    }, []);

    const openEdit = (record) => {
        setEditing(record);
        setModalOpen(true);
        form.setFieldsValue(record);
    };

    const openAdd = () => {
        setEditing(null);
        setModalOpen(true);
        form.resetFields();
    };

    const handleSave = async () => {
        const values = await form.validateFields();
        const res = await saveAccount(values);
        if (res.code === 0) {
            message.success('保存成功');
            setModalOpen(false);
            fetchList();
        } else {
            message.error(res.message || '保存失败');
        }
    };

    const columns = [
        { title: '账户名', dataIndex: 'username', key: 'username', width: 160 },
        { title: '昵称', dataIndex: 'nickname', key: 'nickname', width: 160 },
        {
            title: '角色', dataIndex: 'role', key: 'role', width: 120,
            render: (v) => <Tag color={v === '管理员' ? 'geekblue' : v === '运营' ? 'blue' : 'default'}>{v}</Tag>,
        },
        {
            title: '状态', dataIndex: 'status', key: 'status', width: 90,
            render: (v) => <Tag color={v === '启用' ? 'success' : 'default'}>{v}</Tag>,
        },
        { title: '创建时间', dataIndex: 'create_time', key: 'create_time', width: 180 },
        {
            title: '操作', key: 'action', width: 120,
            onHeaderCell: () => ({ style: { textAlign: 'center' } }),
            render: (_, record) => (
                <Space>
                    {hasPerm('system:account:edit') && (
                        <Button type="link" onClick={() => openEdit(record)}>编辑</Button>
                    )}
                    {hasPerm('system:account:assign') && (
                        <Button type="link" onClick={() => message.info(`分配 ${record.username} 权限`)}>权限分配</Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '16px' }}>
            <div style={{ marginBottom: 16 }}>
                {hasPerm('system:account:add') && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>新增账户</Button>
                )}
            </div>

            <Table rowKey="id" columns={columns} dataSource={dataSource} loading={loading} pagination={false} />

            <Modal
                title={editing ? '编辑账户' : '新增账户'}
                open={modalOpen}
                onOk={handleSave}
                onCancel={() => setModalOpen(false)}
                okText="保存"
                cancelText="取消"
                centered
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="username" label="账户名" rules={[{ required: true, message: '请输入账户名' }]}>
                        <Input placeholder="请输入账户名" disabled={!!editing} />
                    </Form.Item>
                    <Form.Item name="nickname" label="昵称" rules={[{ required: true, message: '请输入昵称' }]}>
                        <Input placeholder="请输入昵称" />
                    </Form.Item>
                    <Form.Item name="role" label="角色" rules={[{ required: true, message: '请选择角色' }]}>
                        <Select placeholder="请选择角色" options={roleOptions} />
                    </Form.Item>
                    <Form.Item name="status" label="状态" initialValue="启用">
                        <Select options={[{ value: '启用', label: '启用' }, { value: '停用', label: '停用' }]} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default AccountManage;
